'use client'

import { GoogleDriveFilePicker } from '@/components/ChatV2/GoogleDriveFilePicker'
import { AttachmentButton } from '@/components/ui/AttachmentButton'
import { useGoogleDriveFileHandler } from '@/hooks/useGoogleDriveFileHandler'
import { useGoogleDriveIntegration } from '@/hooks/useGoogleDriveIntegration'
import { useTooltip } from '@/hooks/useTooltip'
import RightIcon from '@/icons/RightIcon'
import {
  CtrlClickLink,
  handleSpaceInLink,
  handleSpaceOutsideLink,
  insertPlainTextWithLinks,
} from '@/lib/editor/link-utils'
import {
  clearWorkflowExecution,
  setError,
  setFailedMessage,
  setIdle,
  setSending,
} from '@/lib/features/copilotv2/ChatSlice'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { copilotEvents } from '@/lib/features/copilotv2/copilotEvents'
import { getCopilotService } from '@/lib/features/copilotv2/CopilotService'
import { useCreateIssueMutation } from '@/lib/features/issue/issueApi'
import {
  useGetSessionQuery,
  useSetSessionBookmarkMutation,
} from '@/lib/features/session/sessionApi'
import { useGetUserQuery } from '@/lib/features/user/userApi'
import { useAppDispatch, useAppSelector } from '@/lib/hooks'
import { FileWithUploadMetadata, SendMessageOptions } from '@/lib/type'
import { cn } from '@/lib/utils'
import { uploadFileToService } from '@/lib/utils/fileUpload'
import { Placeholder } from '@tiptap/extension-placeholder'
import { liftListItem, splitListItem } from '@tiptap/pm/schema-list'
import { Editor, EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Bookmark, FlagIcon, Settings } from 'lucide-react'
import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FiCloud } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { AgentType, AvailableAgents } from 'sprites-types/api/v2/copilot'
import PersonalizationPanel from '../PersonalizationPanel/PersonalizationPanel'
import AgentDropdown from './AgentDropdown'
import ContextDisplay from './ContextDisplay'
import IssueReportForm from './IssueReportForm'

// Constants
const PROSEMIRROR_MIN_HEIGHT = '32px'
const PROSEMIRROR_MAX_HEIGHT = '480px'

type ChatInputProps = {
  onSendMessageAction: (
    message: string,
    options: { files?: File[]; pipedreamAccountIds?: any }
  ) => Promise<void>
  status: 'idle' | 'sending' | 'streaming' | 'error'
  showThinkingAnimation?: boolean
}

function ContextDisplayWrapper() {
  const { context, updateSelectedNodes, updateSelectedExecutions } = useCopilotContext()

  const selectedNodeIds = context.selectedNodeIds || []
  const selectedExecutionIds = context.selectedExecutionIds || []

  const handleRemoveNode = useCallback(
    (nodeId: string) => {
      const updatedNodes = selectedNodeIds.filter((id) => id !== nodeId)
      updateSelectedNodes(updatedNodes)
    },
    [selectedNodeIds, updateSelectedNodes]
  )

  const handleRemoveExecution = useCallback(
    (executionId: string) => {
      const updatedExecutions = selectedExecutionIds.filter((id) => id !== executionId)
      updateSelectedExecutions(updatedExecutions)
    },
    [selectedExecutionIds, updateSelectedExecutions]
  )

  return (
    <ContextDisplay
      selectedNodeIds={selectedNodeIds}
      nodeId={context.nodeId}
      selectedExecutionIds={selectedExecutionIds}
      workflowRef={context.workflowRef || undefined}
      onRemoveNode={handleRemoveNode}
      onRemoveExecution={handleRemoveExecution}
      showRemoveButtons={true}
    />
  )
}

// Tooltip Button Component with Portal
function TooltipButton({
  onClick,
  title,
  children,
  className,
  disabled = false,
  ...props
}: {
  onClick: React.MouseEventHandler<HTMLButtonElement>
  title: string
  children: React.ReactNode
  className?: string
  disabled?: boolean
  [key: string]: unknown
}) {
  const { showTooltip, tooltipPosition, buttonRef, showTooltipWithDelay, hideTooltip } = useTooltip(
    { position: 'top' }
  )

  return (
    <>
      <button
        ref={buttonRef}
        onClick={onClick}
        onMouseEnter={() => {
          if (!disabled) {
            showTooltipWithDelay()
          }
        }}
        onMouseLeave={hideTooltip}
        className={className}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
      {showTooltip &&
        !disabled &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="font-hanken-grotesk pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            {title}
          </div>,
          document.body
        )}
    </>
  )
}

export default function ChatInput({
  onSendMessageAction,
  status,
  showThinkingAnimation = false,
}: ChatInputProps) {
  const dispatch = useAppDispatch()
  const { workflowState, activeTypewriterMessageId, messages, currentAgent, sessionId } =
    useCopilotContext()
  const { data: session } = useGetSessionQuery(sessionId!, {
    skip: !sessionId,
  })
  const { disconnect, cancelSession, getConnectionStatus } = getCopilotService()
  const failedMessage = useAppSelector((state) => state.streamingChat.failedMessage)
  const { data: userData, isSuccess: isUserSuccess } = useGetUserQuery()
  const workspaceId = userData?.workspace.id
  const [setSessionBookmark] = useSetSessionBookmarkMutation()
  // State declarations
  const [isFocus, setIsFocus] = useState<boolean>(false)
  const [isInputEmpty, setIsInputEmpty] = useState(true)
  const [isCommand, setIsCommand] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<FileWithUploadMetadata[]>([])
  const [fileObjectUrls, setFileObjectUrls] = useState<Map<string, string>>(new Map())
  const [isDragOver, setIsDragOver] = useState<boolean>(false)
  const inputTextRef = useRef('')
  const editorRef = useRef<Editor | null>(null)
  const [isModifierPressed, setIsModifierPressed] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<
    'disconnected' | 'connecting' | 'connected' | 'cancelling' | 'error'
  >('disconnected')
  const currentAgentRef = useRef<AgentType>(currentAgent)
  const [showIssueForm, setShowIssueForm] = useState(false)
  const [showPersonalization, setShowPersonalization] = useState(false)
  const [createIssue, { isLoading: isSubmittingIssue }] = useCreateIssueMutation()

  // Google Drive integration
  const {
    showGoogleDrivePicker,
    googleDriveAccountId,
    handleGoogleDriveClick,
    isGoogleDriveConnected,
    isCheckingGoogleDrive,
    setShowGoogleDrivePicker,
  } = useGoogleDriveIntegration({ userData })

  const isCancelling = connectionStatus === 'cancelling'

  // Update connection status when events occur - SIMPLIFIED
  useEffect(() => {
    const { onEvent, offEvent } = getCopilotService()
    const handleEvent = () => {
      setConnectionStatus(getConnectionStatus())
    }

    onEvent(handleEvent)
    return () => offEvent(handleEvent)
  }, [getConnectionStatus])

  // Prevent navigation/refresh when SSE connection is active
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (connectionStatus === 'connected' || connectionStatus === 'cancelling') {
        const message = 'You have an active copilot session. Are you sure you want to leave?'
        event.preventDefault()
        event.returnValue = message // For older browsers
        return message
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [connectionStatus])

  // Check if any files are currently uploading or downloading
  const hasUploadingFiles = files.some(
    (file) => file.isDownloading === true || file.isUploading === true
  )

  // Compute disabled and isLoading states from the passed props
  const canSubmit: boolean =
    status !== 'sending' &&
    status !== 'streaming' &&
    !isCancelling &&
    isUserSuccess &&
    !!workspaceId &&
    !hasUploadingFiles
  const canStop: boolean =
    connectionStatus === 'connected' && !isCancelling && isUserSuccess && !!workspaceId
  const isLoading: boolean = status === 'sending' || isCancelling

  // Keep latest status in a ref to prevent stale closures without re-rendering
  const statusRef = useRef(status)
  useEffect(() => {
    statusRef.current = status
  }, [status])

  // Refs for other dynamic values used in callbacks to avoid stale closures
  const isUserSuccessRef = useRef(isUserSuccess)
  useEffect(() => {
    isUserSuccessRef.current = isUserSuccess
  }, [isUserSuccess])

  // Keep currentAgent in a ref so the editor doesn't need to be recreated on change
  useEffect(() => {
    currentAgentRef.current = currentAgent
  }, [currentAgent])

  const workspaceIdRef = useRef(workspaceId)
  useEffect(() => {
    workspaceIdRef.current = workspaceId
  }, [workspaceId])

  const isCancellingRef = useRef(isCancelling)
  useEffect(() => {
    isCancellingRef.current = isCancelling
  }, [isCancelling])

  const filesRef = useRef<File[]>(files)
  useEffect(() => {
    filesRef.current = files
  }, [files])

  // Ref to hold current placeholder state for dynamic updates without editor recreation
  const placeholderStateRef = useRef({
    showThinkingAnimation,
    activeTypewriterMessageId,
    messagesLength: messages.length,
  })

  // File handling functions
  const addFiles = useCallback((newFiles: File[]) => {
    // Mark files as uploading
    const filesWithUploadState = newFiles.map((file) => {
      const fileWithMeta = file as FileWithUploadMetadata
      fileWithMeta.isUploading = true
      fileWithMeta.uploadProgress = 0
      return fileWithMeta
    })

    setFiles((currentFiles) => {
      const updatedFiles = [...currentFiles, ...filesWithUploadState]

      // Create object URLs for new files
      setFileObjectUrls((currentUrls) => {
        const newUrls = new Map(currentUrls)
        filesWithUploadState.forEach((file) => {
          const key = `${file.name}-${file.size}-${file.lastModified}`
          if (!newUrls.has(key)) {
            newUrls.set(key, URL.createObjectURL(file))
          }
        })
        return newUrls
      })

      return updatedFiles
    })

    // Upload files immediately with progress tracking
    filesWithUploadState.forEach(async (file) => {
      const key = `${file.name}-${file.size}-${file.lastModified}`

      try {
        const result = await uploadFileToService(file, (progress) => {
          setFiles((currentFiles) =>
            currentFiles.map((f) => {
              if (
                f.name === file.name &&
                f.size === file.size &&
                f.lastModified === file.lastModified
              ) {
                const updated = f as FileWithUploadMetadata
                updated.uploadProgress = progress
                return updated
              }
              return f
            })
          )
        })

        if (!result.success || !result.url) {
          throw new Error(result.error || 'File upload failed')
        }

        // Check if file still exists before updating (prevents race condition)
        setFiles((currentFiles) => {
          const fileStillExists = currentFiles.some(
            (f) =>
              f.name === file.name && f.size === file.size && f.lastModified === file.lastModified
          )
          if (!fileStillExists) {
            // File was removed while upload was in progress, don't update
            return currentFiles
          }

          return currentFiles.map((f) => {
            if (
              f.name === file.name &&
              f.size === file.size &&
              f.lastModified === file.lastModified
            ) {
              const updated = f as FileWithUploadMetadata
              updated.isUploading = false
              updated.uploadProgress = 100
              updated.uploadedUrl = result.url
              return updated
            }
            return f
          })
        })
      } catch (error) {
        console.error('File upload failed:', error)
        // Remove file on upload failure and clean up object URL
        setFiles((currentFiles) =>
          currentFiles.filter(
            (f) =>
              !(
                f.name === file.name &&
                f.size === file.size &&
                f.lastModified === file.lastModified
              )
          )
        )
        // Revoke object URL to prevent memory leak
        setFileObjectUrls((currentUrls) => {
          const newUrls = new Map(currentUrls)
          const url = newUrls.get(key)
          if (url) {
            URL.revokeObjectURL(url)
            newUrls.delete(key)
          }
          return newUrls
        })

        // Show the actual error message from the backend/upload service
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload file'
        toast.error(errorMessage)
      }
    })
  }, [])

  const removeFile = useCallback((fileToRemove: File) => {
    const key = `${fileToRemove.name}-${fileToRemove.size}-${fileToRemove.lastModified}`

    setFiles((currentFiles) => currentFiles.filter((f) => f !== fileToRemove))

    // Clean up object URL
    setFileObjectUrls((currentUrls) => {
      const newUrls = new Map(currentUrls)
      const url = newUrls.get(key)
      if (url) {
        URL.revokeObjectURL(url)
        newUrls.delete(key)
      }
      return newUrls
    })
  }, [])

  const clearFiles = useCallback(() => {
    // Clean up all object URLs
    fileObjectUrls.forEach((url) => URL.revokeObjectURL(url))
    setFileObjectUrls(new Map())
    setFiles([])
  }, [fileObjectUrls])

  const { handleGoogleDriveSendMessage } = useGoogleDriveFileHandler({
    setFiles,
    addFiles,
    setFileObjectUrls,
  })

  // Update placeholder state ref on each render to ensure fresh values
  useEffect(() => {
    placeholderStateRef.current = {
      showThinkingAnimation,
      activeTypewriterMessageId,
      messagesLength: messages.length,
    }
  })

  // Get agent display name
  const getAgentDisplayName = useCallback((agent: AgentType): string => {
    const agentInfo = AvailableAgents.find((a) => a.type === agent)
    return agentInfo?.name || 'Workflow Planner'
  }, [])

  // Create a stable placeholder function that won't change between renders
  const getPlaceholderText = useCallback(() => {
    const state = placeholderStateRef.current
    const agentName = getAgentDisplayName(currentAgentRef.current)

    if (state.showThinkingAnimation) {
      return `${agentName} is thinking...`
    }

    if (state.activeTypewriterMessageId) {
      return `${agentName} is responding...`
    }

    // Different placeholders based on chat thread length
    if (state.messagesLength === 0) {
      return `Ask ${agentName} what should we automate today?`
    } else {
      return `Continue your conversation with ${agentName} - and just ask if you need help!`
    }
  }, [getAgentDisplayName])

  const handleStop = useCallback(async () => {
    try {
      // Cancel the session via Redis Pub/Sub with a 60-second timeout
      // Timeout is longer to accommodate non-cancellable operations like image generation
      // which can take 10-30 seconds and cannot be cancelled without eating the cost
      const cancelPromise = cancelSession()
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Cancellation timeout')), 60000)
      )

      await Promise.race([cancelPromise, timeoutPromise])

      // Clear local state
      dispatch(clearWorkflowExecution())
      dispatch(setIdle())
      workflowState.resetState()

      toast.success('Operation stopped')
    } catch (error) {
      console.error('Error during stop operation:', error)

      if (error instanceof Error && error.message === 'Cancellation timeout') {
        toast.error('Cancellation timed out after 60 seconds, forcing disconnect')
      } else {
        toast.error('Error stopping operation')
      }

      // Fallback to local disconnect if cancellation fails or times out
      disconnect()
      dispatch(clearWorkflowExecution())
      dispatch(setIdle())
      workflowState.resetState()
    }
  }, [cancelSession, disconnect, dispatch, workflowState])

  const handleSubmit = useCallback(
    async (text: string) => {
      const effectiveFiles = filesRef.current
      const currentStatus = statusRef.current
      const canSubmitNow =
        currentStatus === 'idle' &&
        !isCancellingRef.current &&
        isUserSuccessRef.current &&
        !!workspaceIdRef.current

      if (!canSubmitNow || (!text.trim() && effectiveFiles.length === 0)) {
        return
      }

      const message = text || inputTextRef.current || ''

      // Filter out unsupported files before sending
      const supportedFiles = effectiveFiles.filter(
        (file) => !(file as FileWithUploadMetadata).isUnsupported
      )

      // Store the message and files for potential restoration on failure
      const previousMessage = message
      const previousFiles = [...effectiveFiles]

      // Clear input immediately for better UX
      editorRef.current?.commands.clearContent()
      setIsInputEmpty(true)
      setIsCommand(false)
      clearFiles()
      inputTextRef.current = ''

      dispatch(setSending())

      try {
        const options: SendMessageOptions = {
          files: supportedFiles,
        } satisfies SendMessageOptions

        await onSendMessageAction(message, options)
      } catch (error) {
        console.error('Failed to send message:', error)

        // Reset status to error to prevent stuck 'sending' status
        const errorMessage = error instanceof Error ? error.message : 'Failed to send message'
        dispatch(setError(errorMessage))

        // Store failed message in Redux for retry functionality
        dispatch(
          setFailedMessage({
            content: previousMessage,
            files: previousFiles.length > 0 ? previousFiles : undefined,
          })
        )

        // Restore the input content for retry
        editorRef.current?.commands.setContent(previousMessage)
        setIsInputEmpty(false)
        setIsCommand(previousMessage.trim().startsWith('/'))
        setFiles(previousFiles)
        // Recreate object URLs for restored files
        setFileObjectUrls(
          new Map(
            previousFiles.map((file) => {
              const key = `${file.name}-${file.size}-${file.lastModified}`
              return [key, URL.createObjectURL(file)]
            })
          )
        )
        inputTextRef.current = previousMessage
      }
    },
    [dispatch, onSendMessageAction, clearFiles]
  )

  // Tiptap editor (create only once)
  const editor = useEditor(
    {
      immediatelyRender: false, // Fix SSR hydration mismatch
      extensions: [
        StarterKit.configure({
          // Enable essential markdown features for chat (omitted = enabled)
          // code: {},           // Inline `code` - enabled by default
          // codeBlock: {},      // ```code blocks``` - enabled by default
          // bulletList: {},     // • bullet lists - enabled by default
          // orderedList: {},    // 1. numbered lists - enabled by default
          // listItem: {},       // Required for lists - enabled by default
          // blockquote: {},     // > quotes - enabled by default

          // Disable built-in bold/italic
          bold: false,
          italic: false,

          // Keep these disabled for chat input
          heading: false, // # headings not needed
          horizontalRule: false, // --- not useful in chat
          strike: false, // ~~strikethrough~~ less common
        }),
        Placeholder.configure({
          placeholder: ({ node, editor }) => {
            // Only show placeholder in empty document, not in list items or other nested nodes
            if (node.type.name === 'listItem') {
              return ''
            }
            if (node.type.name === 'blockquote') {
              return ''
            }

            // Only show in main document paragraph when editor is completely empty
            if (node.type.name === 'paragraph' && editor.isEmpty) {
              return getPlaceholderText()
            }

            return ''
          },
          showOnlyWhenEditable: true,
        }),
        CtrlClickLink.configure({
          autolink: false,
          openOnClick: false,
          linkOnPaste: false,
          HTMLAttributes: {
            rel: 'noopener noreferrer',
            target: '_blank',
            class: 'text-blue-600 underline hover:text-blue-800',
          },
        }).extend({
          // Override to make space key exit the link mark
          addKeyboardShortcuts() {
            return {
              Space: ({ editor }: { editor: Editor }) => {
                // Check if we're in a link - if so, split it
                if (editor.isActive('link')) {
                  return handleSpaceInLink(editor)
                }
                // If not, check if the text before cursor is a URL and link it
                return handleSpaceOutsideLink(editor)
              },
            }
          },
        }),
      ],
      content: '',
      editable: true, // Always allow typing
      onUpdate: ({ editor }) => {
        const text = editor.getText()
        const trimmedText = text.trim()
        setIsInputEmpty(trimmedText === '')
        setIsCommand(trimmedText.startsWith('/'))
        // Extract text while preserving hard breaks as newlines
        let textWithNewlines = ''
        editor.state.doc.descendants((node, _pos) => {
          if (node.type.name === 'hardBreak') {
            textWithNewlines += '\n'
          } else if (node.isText && node.text) {
            textWithNewlines += node.text
          } else if (node.type.name === 'paragraph' && textWithNewlines.length > 0) {
            // Add newline between paragraphs (but not before the first one)
            textWithNewlines += '\n'
          }
          return true
        })
        inputTextRef.current = textWithNewlines
      },
      onFocus: () => setIsFocus(true),
      onBlur: () => {
        setIsFocus(false)
        // Reset modifier state when editor loses focus to prevent stuck highlighting
        setIsModifierPressed(false)
      },
      onCreate: () => {
        // Set initial empty state
        setIsInputEmpty(true)
        setIsCommand(false)
      },
      editorProps: {
        handlePaste: (view, event) => {
          // Always paste as plain text, stripping all formatting including links
          const plainText = event.clipboardData?.getData('text/plain')
          if (plainText) {
            event.preventDefault()
            insertPlainTextWithLinks(view, plainText)
            return true
          }
          return false
        },
        handleKeyDown: (view, event) => {
          // Handle Enter to send message (but not if Shift is held)
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()

            // Get the actual editor text content directly to ensure we have the latest
            const editorText = view.state.doc.textContent || ''
            const currentInputText = inputTextRef.current || ''

            // Use inputRef as source of truth (it has newlines preserved)
            const textToSubmit = currentInputText || editorText
            void handleSubmit(textToSubmit)
            return true
          }

          // Handle Shift+Enter for lists and newlines
          if (event.key === 'Enter' && event.shiftKey) {
            const { state } = view
            const { selection } = state
            const { $from } = selection

            // Check if we're in a list item
            let listItemPos = null
            let listItemNode = null

            for (let d = $from.depth; d >= 0; d--) {
              const node = $from.node(d)
              if (node.type.name === 'listItem') {
                listItemPos = $from.start(d)
                listItemNode = node
                break
              }
            }

            if (listItemNode && listItemPos !== null) {
              const listItemType = state.schema.nodes.listItem

              // Check if the list item is empty (only has empty paragraph)
              const isEmpty =
                listItemNode.content.size === 0 ||
                (listItemNode.content.size === 2 && // paragraph node (2 is typical size for empty paragraph)
                  listItemNode.firstChild?.type.name === 'paragraph' &&
                  listItemNode.firstChild?.content.size === 0)

              if (isEmpty) {
                // Exit the list by lifting the empty list item
                const liftCommand = liftListItem(listItemType)
                if (liftCommand(state)) {
                  event.preventDefault()
                  liftCommand(state, view.dispatch)
                  return true
                }
              } else {
                // Create a new list item
                const splitCommand = splitListItem(listItemType)
                if (splitCommand(state)) {
                  event.preventDefault()
                  splitCommand(state, view.dispatch)
                  return true
                }
              }
            }

            // If not in list or list handling failed, just create a line break
            // Let the default behavior handle this (hard break)
            return false
          }

          return false
        },
      },
    },
    []
  )

  // Set editor ref when editor is created (only once)
  useEffect(() => {
    if (editor && !editorRef.current) {
      editorRef.current = editor
    }

    return () => {
      if (editorRef.current && !editorRef.current.isDestroyed) {
        editorRef.current.destroy()
      }
      editorRef.current = null
    }
  }, [editor])

  // Create stable callback for rollback functionality
  const setInputContent = useCallback(
    (content: string) => {
      if (!editor) return

      // Handle null/undefined more explicitly since || '' isn't catching all cases
      const safeContent = content != null ? String(content) : ''

      // Avoid unnecessary updates if content hasn't changed
      // Compare actual editor content instead of ref value to catch rich text differences
      if (editor.getText() === safeContent) {
        return
      }

      editor.commands.setContent(safeContent)
      // Defer setState to avoid "setState in render" error
      setTimeout(() => {
        const trimmedContent = safeContent.trim()
        setIsInputEmpty(!trimmedContent)
        setIsCommand(trimmedContent.startsWith('/'))
      }, 0)
      inputTextRef.current = safeContent
    },
    [editor, setIsInputEmpty]
  )
  // Listen for rollback events to update input content and files
  useEffect(() => {
    if (editor) {
      const cleanup = copilotEvents.addEventListener(async (event) => {
        if (event.type === 'rollback') {
          // Ensure connection is properly disconnected on rollback
          disconnect()

          // Reset status to idle
          dispatch(setIdle())

          setInputContent(event.data.content)

          // Restore files if they exist in the rollback data
          if (event.data.files && event.data.files.length > 0) {
            try {
              // Convert file URLs back to File objects
              const filePromises = event.data.files.map(async (fileData) => {
                try {
                  const response = await fetch(fileData.url)
                  if (!response.ok) {
                    throw new Error(`Failed to fetch file: ${response.status}`)
                  }
                  const blob = await response.blob()

                  // Create a File object from the blob
                  const file = new File([blob], fileData.filename, {
                    type: blob.type || 'application/octet-stream',
                    lastModified: Date.now(),
                  })

                  return file
                } catch (error) {
                  console.error('Failed to restore file:', error)
                  return null
                }
              })

              const restoredFiles = await Promise.all(filePromises)
              const validFiles = restoredFiles.filter((file) => file !== null) as File[]

              if (validFiles.length > 0) {
                setFiles(validFiles)

                // Create object URLs for restored files
                setFileObjectUrls(
                  new Map(
                    validFiles.map((file) => {
                      const key = `${file.name}-${file.size}-${file.lastModified}`
                      return [key, URL.createObjectURL(file)]
                    })
                  )
                )
              }
            } catch (error) {
              console.error('Error restoring files from rollback:', error)
            }
          } else {
            // Clear files if no files in rollback
            setFiles([])
            fileObjectUrls.forEach((url) => URL.revokeObjectURL(url))
            setFileObjectUrls(new Map())
          }
        }
      })

      // Cleanup event listener on unmount
      return cleanup
    }
  }, [editor, setInputContent, setFiles, setFileObjectUrls, fileObjectUrls, dispatch])

  // Restore input when error occurs and we have a failed message
  useEffect(() => {
    if (status === 'error' && failedMessage && editor) {
      // Only restore if input is currently empty to avoid overwriting user's new input
      const currentContent = editor.getText().trim()
      if (!currentContent) {
        editor.commands.setContent(failedMessage.content)
        setIsInputEmpty(false)
        setIsCommand(failedMessage.content.trim().startsWith('/'))
        inputTextRef.current = failedMessage.content

        // Restore files if they exist
        if (failedMessage.files && failedMessage.files.length > 0) {
          setFiles(failedMessage.files)
          // Recreate object URLs for restored files
          setFileObjectUrls(
            new Map(
              failedMessage.files.map((file) => {
                const key = `${file.name}-${file.size}-${file.lastModified}`
                return [key, URL.createObjectURL(file)]
              })
            )
          )
        }
      }
    }
  }, [status, failedMessage, editor])

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      fileObjectUrls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [fileObjectUrls])

  // Track modifier keys for dynamic cursor
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey) {
        setIsModifierPressed(true)
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      // Reset if neither modifier key is pressed
      // Use setTimeout to handle rapid key sequences and ensure proper cleanup
      setTimeout(() => {
        if (!event.ctrlKey && !event.metaKey) {
          setIsModifierPressed(false)
        }
      }, 0)
    }

    const handleBlur = () => {
      setIsModifierPressed(false)
    }

    // Additional cleanup for edge cases
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsModifierPressed(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)
    window.addEventListener('blur', handleBlur)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('blur', handleBlur)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Focus management is handled by Tiptap's onFocus/onBlur callbacks

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    if (selectedFiles.length > 0) {
      addFiles(selectedFiles)
    }
  }

  const handleIssueSubmit = async (data: {
    title: string
    description: string
    imageUrl?: string
  }) => {
    try {
      await createIssue(data).unwrap()
      toast.success('Issue reported successfully! Thank you for your feedback.')
    } catch (error) {
      console.error('Failed to submit issue:', error)
      toast.error('Failed to submit issue. Please try again.')
      throw error // Re-throw to prevent form reset
    }
  }

  // handleRemoveFile replaced by removeFile function above

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length > 0) {
      addFiles(droppedFiles)
    }
  }

  return (
    <div
      className={cn(
        'box-border flex flex-col items-start rounded-2xl border',
        'order-1 flex-none flex-grow-0 gap-2 self-stretch p-4',
        {
          // Command styling takes precedence
          'border-blue-200 bg-blue-50': isCommand && !isDragOver,
          // Drag over styling
          'border-blue-400 bg-blue-50': isDragOver,
          // Normal focus/unfocus styling
          'border-[#B0B0B0] bg-[#F3F3F3]': isFocus && !isCommand && !isDragOver,
          'border-[#E0E0E0] bg-[#F3F3F3]': !isFocus && !isCommand && !isDragOver,
        }
      )}
      style={{ transition: 'border-color 0.2s, background-color 0.2s' }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Files preview */}
      {files.length > 0 && (
        <div className="mb-2 flex max-w-full flex-col gap-2">
          {/* Files container - responsive flex layout */}
          <div className="flex flex-wrap gap-2">
            {files.map((file) => {
              const fileKey = `${file.name}-${file.size}-${file.lastModified}`
              const fileObjectUrl = fileObjectUrls.get(fileKey)
              const fileWithMeta = file as FileWithUploadMetadata
              const isDownloading = fileWithMeta.isDownloading === true
              const isUploading = fileWithMeta.isUploading === true
              const uploadProgress = fileWithMeta.uploadProgress ?? 0
              const isUnsupported = fileWithMeta.isUnsupported === true
              const isProcessing = isDownloading || isUploading

              return (
                <div
                  key={fileKey}
                  className={cn(
                    'relative flex min-w-0 flex-1 basis-full items-center gap-2 rounded border p-2 shadow-sm sm:basis-auto',
                    isUnsupported
                      ? 'border-red-200 bg-red-50 opacity-75'
                      : 'border-gray-200 bg-white'
                  )}
                >
                  {/* Upload progress bar */}
                  {isUploading && uploadProgress < 100 && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden rounded-b bg-gray-100">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  )}

                  <div className="relative">
                    {file.type.startsWith('image/') ? (
                      fileObjectUrl && (
                        <Image
                          src={fileObjectUrl}
                          alt={file.name}
                          className="h-10 w-10 rounded object-cover"
                          width={40}
                          height={40}
                        />
                      )
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200">
                        <span className="text-xs text-gray-600">
                          {file.name.split('.').pop()?.toUpperCase() || 'FILE'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-gray-800">{file.name}</div>
                    <div className="flex items-center gap-1 truncate text-xs">
                      {isUnsupported ? (
                        <span className="font-medium text-red-600">
                          {fileWithMeta.unsupportedReason || 'Unsupported file type'}
                        </span>
                      ) : isDownloading ? (
                        <span className="text-gray-500">Downloading...</span>
                      ) : isUploading ? (
                        <span className="text-gray-500">Uploading {uploadProgress}%</span>
                      ) : (
                        <>
                          <FiCloud className="h-3 w-3 text-gray-500" />
                          <span className="text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ml-2 text-gray-400 hover:text-red-500"
                    aria-label="Remove file"
                    onClick={() => removeFile(file)}
                    disabled={isProcessing}
                  >
                    ×
                  </button>
                </div>
              )
            })}
          </div>
          {/* File summary outside grid */}
          {files.length > 1 && (
            <div className="text-center text-xs text-gray-500">
              {files.length} file{files.length !== 1 ? 's' : ''} selected
              <span> ({(files.reduce((sum, f) => sum + f.size, 0) / 1024).toFixed(1)} KB)</span>
            </div>
          )}
        </div>
      )}
      {/* Context display */}
      <ContextDisplayWrapper />

      {/* Issue Report Form */}
      <IssueReportForm
        isOpen={showIssueForm}
        onClose={() => setShowIssueForm(false)}
        onSubmit={handleIssueSubmit}
        isSubmitting={isSubmittingIssue}
      />

      {/* Personalization Panel */}
      <PersonalizationPanel
        isOpen={showPersonalization}
        onClose={() => setShowPersonalization(false)}
      />

      {googleDriveAccountId && (
        <div className={cn('w-full', showGoogleDrivePicker && 'mb-2')}>
          <GoogleDriveFilePicker
            filePickerInfo={{
              accountId: googleDriveAccountId,
              limit: 0,
            }}
            sendMessage={handleGoogleDriveSendMessage}
            onClose={() => setShowGoogleDrivePicker(false)}
            isVisible={showGoogleDrivePicker}
          />
        </div>
      )}

      {/* Cancellation indicator */}
      {isCancelling && (
        <div className="mb-2 flex items-center justify-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" />
          <span>Stopping operation...</span>
        </div>
      )}

      {/* Input row */}
      <div className="flex w-full items-center">
        <div className="relative w-full">
          <div
            className={cn(
              '[&_.ProseMirror]:font-hanken-grotesk w-full overflow-y-auto border-none bg-transparent text-base shadow-none focus:outline-none focus:ring-0 [&_.ProseMirror]:break-words [&_.ProseMirror]:p-0 [&_.ProseMirror]:leading-normal [&_.ProseMirror]:outline-none',
              {
                'text-blue-700': isCommand,
                'text-[#222]': !isCommand,
              },
              // List styling
              '[&_.ProseMirror_ul]:my-2 [&_.ProseMirror_ul]:ml-4 [&_.ProseMirror_ul]:list-disc',
              '[&_.ProseMirror_ol]:my-2 [&_.ProseMirror_ol]:ml-4 [&_.ProseMirror_ol]:list-decimal',
              '[&_.ProseMirror_li]:my-1',
              // Blockquote styling
              '[&_.ProseMirror_blockquote]:my-2 [&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-gray-300 [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:italic',
              // Code styling
              '[&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:bg-gray-100 [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:font-mono [&_.ProseMirror_code]:text-sm',
              '[&_.ProseMirror_pre]:my-2 [&_.ProseMirror_pre]:overflow-auto [&_.ProseMirror_pre]:rounded [&_.ProseMirror_pre]:bg-gray-100 [&_.ProseMirror_pre]:p-3',
              '[&_.ProseMirror_pre_code]:bg-transparent [&_.ProseMirror_pre_code]:p-0',
              // Bold and italic styling
              '[&_.ProseMirror_strong]:font-bold',
              '[&_.ProseMirror_em]:italic',
              {
                '[&_.ProseMirror_a]:cursor-pointer': isModifierPressed,
                '[&_.ProseMirror_a]:cursor-text': !isModifierPressed,
              }
            )}
            style={{ minHeight: PROSEMIRROR_MIN_HEIGHT, maxHeight: PROSEMIRROR_MAX_HEIGHT }}
          >
            <EditorContent editor={editor} className="w-full" />
          </div>
        </div>
      </div>

      {/* Bottom bar: Agent dropdown, actions, and send button */}
      <div className="flex w-full items-center gap-x-2">
        {/* Agent Dropdown - leftmost position */}
        <AgentDropdown />

        <AttachmentButton
          onGoogleDriveClick={handleGoogleDriveClick}
          fileInputRef={fileInputRef}
          dropdownPosition="top-left"
          disabled={!canSubmit}
          isGoogleDriveConnected={isGoogleDriveConnected}
          isCheckingGoogleDrive={isCheckingGoogleDrive}
        />
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          tabIndex={-1}
          disabled={!canSubmit}
          accept="*"
          multiple
        />
        <TooltipButton
          className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-[#E0E0E0] focus:bg-[#E0E0E0]"
          aria-label="Report issue"
          title="Report issue"
          tabIndex={0}
          role="button"
          onClick={() => setShowIssueForm(!showIssueForm)}
        >
          <FlagIcon className="h-4 w-4 text-[#757575]" />
        </TooltipButton>
        <TooltipButton
          className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-[#E0E0E0] focus:bg-[#E0E0E0]"
          aria-label="Personalization"
          title="Personalization"
          tabIndex={0}
          role="button"
          onClick={() => setShowPersonalization(!showPersonalization)}
        >
          <Settings className="h-4 w-4 text-[#757575]" />
        </TooltipButton>
        {sessionId && session && (
          <TooltipButton
            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-[#E0E0E0] focus:bg-[#E0E0E0]"
            aria-label={session.isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            title={session.isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            tabIndex={0}
            role="button"
            onClick={async (e) => {
              e.preventDefault()
              e.stopPropagation()
              try {
                await setSessionBookmark({
                  sessionId,
                  bookmarked: !session.isBookmarked,
                }).unwrap()
              } catch (error) {
                console.error('Failed to update bookmark', error)
                toast.error('Failed to update bookmark')
              }
            }}
          >
            <Bookmark
              className={cn(
                'h-4 w-4 transition-colors duration-200',
                session.isBookmarked
                  ? 'fill-blue-600 text-blue-600'
                  : 'text-[#757575] hover:text-blue-600'
              )}
              strokeWidth={2}
            />
          </TooltipButton>
        )}
        <span className="flex-1"></span>
        <button
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
            !canSubmit && !canStop
              ? 'cursor-not-allowed bg-[#E0E0E0]'
              : isFocus
                ? 'bg-[#333] hover:bg-[#444]'
                : 'bg-[#222] hover:bg-[#444]'
          )}
          onClick={() => {
            if (canStop) {
              handleStop()
            } else if (canSubmit) {
              void handleSubmit(inputTextRef.current || '')
            }
          }}
          aria-label={isCancelling ? 'Stopping operation...' : 'Send message'}
          style={{ boxShadow: '0 1px 4px 0 rgba(0,0,0,0.08)' }}
          disabled={!canSubmit && !canStop}
          tabIndex={isInputEmpty ? -1 : 0}
        >
          {isLoading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : canStop ? (
            <div className="h-3.5 w-3.5 rounded-[2px] bg-white" />
          ) : (
            <RightIcon className="text-white" />
          )}
        </button>
      </div>
    </div>
  )
}
